import { useEffect, useMemo, useState } from 'react';

import { ActionFunctionArgs, LoaderFunctionArgs, unstable_parseMultipartFormData } from '@remix-run/node';
import { Link, useParams, useRevalidator, useSearchParams } from '@remix-run/react';
import { s3UploadHandler } from '~/.server/services/s3';
import CountdownTimer from '~/components/countdown-time';
import CustomPagination from '~/components/custom-pagination';
import { Home } from '~/components/images/Home';
import Layout from '~/components/layout';
import { OptimizedProductCard } from '~/components/optimized-product-card';
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '~/components/ui/breadcrumb';
import { Button } from '~/components/ui/button';
import Viewer from '~/components/ui/rich-text/viewer';
import { cn, formatDateTime, formatFileSize, getCategoryName, handleZodError } from '~/lib/utils';
import { getAuctionRegistrationByUserIdAndAuctionId } from '~/services/api/auction-registrations';
import { getAuctionDetailsBasic } from '~/services/api/auctions';
import { validateLuciaSession, validateUser } from '~/services/api/auth';
import { optimizedGetProductList } from '~/services/api/products';
import { AUCTION_EVENTS, validateAuctionEvent } from '~/services/events/auction-events';
import { USER_EVENTS, validateUserEvent } from '~/services/events/user-events';
import { AuctionRegistrationStatus, AuctionType } from '~/types';
import { Check, Dot, Download, File } from 'lucide-react';
import Pusher from 'pusher-js';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';
import { SpinnerCircularFixed } from 'spinners-react';

import AuctionRegistration from '../product-detail.$slug/auction-registration';

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { session } = await validateLuciaSession(request);
  const { user } = await validateUser(request);

  const url = new URL(request.url);
  const limit = Number(url.searchParams.get('limit')) || 12;
  const page = Number(url.searchParams.get('page')) || undefined;

  const slug = params.slug as string;
  const auction = await getAuctionDetailsBasic(slug);

  const starLots = await optimizedGetProductList({
    auctionId: auction?.data?.id || '',
    limit,
    page,
    sortByLotNumber: true,
    starLot: true,
  });

  if (!auction.data) {
    throw new Response('Not Found', { status: 404 });
  }

  const auctionRegistration = user ? await getAuctionRegistrationByUserIdAndAuctionId(user.id, auction.data.id) : null;

  return typedjson({
    user,
    session,
    auction: auction.data,
    auctionRegistration,

    starLots,
    limit,
  });
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const contentType = request.headers.get('Content-Type');

  try {
    if (contentType && contentType?.includes('multipart/form-data')) {
      const multipartFormData = await unstable_parseMultipartFormData(request, s3UploadHandler);

      const document = multipartFormData.get('document');
      const paymentDocument = multipartFormData.get('paymentDocument');

      return typedjson({
        document,
        paymentDocument,
      });
    }
  } catch (error) {
    console.error(error);
    if (error instanceof Error) {
      return typedjson({ error: { message: error.message } });
    }
  }
};

export default function AuctionDetailStarLots() {
  const data = useTypedLoaderData<typeof loader>();
  const { auction, starLots, user, session, auctionRegistration, limit } = data;
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const revalidator = useRevalidator();

  const [activeTab, setActiveTab] = useState('collections');
  const [isAwaitingApproval, setIsAwaitingApproval] = useState(false);
  const [isAuctionRegistered, setIsAuctionRegistered] = useState(false);

  const auctionEnded = useMemo(() => new Date() >= new Date(auction.auctionEnd), [auction]);
  const isAuctionLive = useMemo(() => {
    if (auctionEnded || auction.auctionType !== AuctionType.TIME2LIVE) return false;
    return auction.products.some((product) => product.liveBiddingMode);
  }, [auction, auctionEnded]);

  const handleTimeEnd = () => {
    // K2: Give a slight delay to avoid a race condition
    setTimeout(() => {
      revalidator.revalidate();
    }, 1000);
  };

  useEffect(() => {
    const pusher = new Pusher(import.meta.env.VITE_PUSHER_KEY, {
      cluster: 'ap1',
    });

    if (user) {
      const userChannel = pusher.subscribe(`user-${user.id}`);
      userChannel.bind(USER_EVENTS.AuctionRegistrationApproved, (payload: unknown) => {
        try {
          const { data } = validateUserEvent({
            event: USER_EVENTS.AuctionRegistrationApproved,
            data: payload,
          });

          if (data.auctionId === auction.id) {
            revalidator.revalidate();
          }
        } catch (err) {
          handleZodError(err);
        }
      });
    }

    const auctionChannel = pusher.subscribe(`auction-${auction.id}`);
    auctionChannel.bind(AUCTION_EVENTS.LiveBiddingStarted, (payload: unknown) => {
      try {
        validateAuctionEvent({
          event: AUCTION_EVENTS.LiveBiddingStarted,
          data: payload,
        });

        revalidator.revalidate();
      } catch (err) {
        handleZodError(err);
      }
    });

    return () => {
      pusher.unbind(USER_EVENTS.AuctionRegistrationApproved);
      pusher.unsubscribe(`user-${user?.id}`);
      pusher.disconnect();
    };
  }, []);

  useEffect(() => {
    if (
      auctionRegistration &&
      auctionRegistration.userId === user?.id &&
      auctionRegistration.status === AuctionRegistrationStatus.PENDING &&
      auctionRegistration.auctionId === data.auction.id
    ) {
      setIsAwaitingApproval(true);
    }
    if (
      auctionRegistration &&
      auctionRegistration.userId === user?.id &&
      auctionRegistration.status === AuctionRegistrationStatus.APPROVED &&
      auctionRegistration.auctionId === data.auction.id
    ) {
      setIsAwaitingApproval(false);
      setIsAuctionRegistered(true);
    }
  }, [auctionRegistration, user, session, auction, data.auction.id]);

  const currentPage = data.starLots.page;
  const totalPages = Math.ceil(data.starLots.totalCount / limit);

  return (
    <Layout user={user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/" className="flex flex-row items-center gap-1 text-sm font-normal">
              <Home className="h-5 w-5" />
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/auctions" className="text-sm font-normal">
              Auctions
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />

          <BreadcrumbLink href={`/auction-detail/${auction.slug}`} className="text-sm font-normal">
            {auction.name}
          </BreadcrumbLink>

          <BreadcrumbSeparator />
          <BreadcrumbPage>
            <BreadcrumbLink className="text-sm font-normal">Star Lots</BreadcrumbLink>
          </BreadcrumbPage>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mt-4 h-[1px] bg-slate-200 lg:mt-6" />

      <div className="grid grid-cols-2 gap-4 bg-gray-50 p-6">
        <div className="col-span-2 lg:col-span-1">
          {/* desktop */}
          <div className="hidden flex-row items-center gap-1 text-xs font-normal text-slate-500 lg:flex">
            <p>{getCategoryName(starLots.list?.[0]?.categoryName || '')}</p>
            <Dot className="h-3 w-3" />
            <p>{auction.auctionType === AuctionType.TIMEONLINE ? 'TimeOnline' : 'Time2Live'}</p>
            <Dot className="h-3 w-3" />
            <p>Start: {formatDateTime(auction?.auctionStart)} GMT+8</p>
          </div>
          {/* Mobile */}
          <div className="flex flex-col items-start gap-1 text-xs font-normal text-slate-500 lg:hidden">
            <div className="flex flex-row">
              <p>{getCategoryName(starLots.list?.[0]?.categoryName || '')}</p>
              <Dot className="h-3 w-3" />
              <p>{auction.auctionType === AuctionType.TIMEONLINE ? 'TimeOnline' : 'Time2Live'}</p>
            </div>
            <div className="flex flex-col">
              <p>Start: {formatDateTime(auction?.auctionStart)} GMT+8</p>
            </div>
          </div>
          {/*  */}
          <div className="my-4 flex flex-col">
            <p className="mb-4 break-words text-2xl font-medium">{auction.name}</p>
            <Link to={`/auction-house/${auction.auctionHouse?.slug}`}>
              <div className="flex flex-row items-center gap-2">
                <Avatar className="h-8 w-8 border">
                  <AvatarImage src={auction.auctionHouse.image} alt={auction.auctionHouse.name} />
                  <AvatarFallback className="bg-black text-white">
                    {auction.auctionHouse?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <p className="text-sm font-normal text-slate-900 hover:underline">{auction.auctionHouse?.name}</p>
              </div>
            </Link>
          </div>
          <Viewer content={auction.description} />
        </div>
        {auction.auctionType === AuctionType.TIMEONLINE ? (
          <div className="col-span-2 justify-end lg:col-span-1">
            <div />
            <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-slate-200 bg-white p-4 text-center">
              <div className="flex flex-col items-center justify-center gap-2">
                <p className="-mb-1.5 text-pretty text-xs font-normal text-slate-500">Auction Start</p>
                <p className="text-sm font-semibold text-slate-900">{formatDateTime(auction.auctionStart)} GMT+8</p>
              </div>
              <div className="flex flex-col items-center justify-center gap-2">
                <p className="-mb-1.5 text-pretty text-xs font-normal text-slate-500">Auction Ending From</p>
                <p className="text-sm font-semibold text-slate-900">{formatDateTime(auction.auctionEnd)} GMT+8</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="col-span-2 justify-end lg:col-span-1 lg:pl-60">
            <div className="flex flex-col justify-center gap-3 border border-slate-200 bg-white p-6">
              <p className="text-center text-xs font-normal">
                {isAuctionLive ? (
                  <span className="text-red-500">Live auction starts in:</span>
                ) : (
                  'Online bidding starts in:'
                )}
              </p>
              <CountdownTimer startDate={auction ? auction.auctionStart : ''} onTimeEnd={handleTimeEnd} />

              {auctionEnded ? (
                <Button variant="outline" className="pointer-events-none mt-4 hover:bg-white">
                  Auction Ended
                </Button>
              ) : !isAuctionRegistered && !isAwaitingApproval ? (
                <AuctionRegistration
                  data={data.auction as unknown as Awaited<ReturnType<typeof getAuctionDetailsBasic>>['data']}
                  userId={user?.id}
                  isLoggedIn={session ? true : false}
                />
              ) : isAuctionLive && user && isAuctionRegistered && !isAwaitingApproval ? (
                <Link to={`/auction-live/${slug}`}>
                  <Button variant={'primary'} className="w-full">
                    <p className="text-center text-sm font-semibold">Enter Live Auction</p>
                  </Button>
                </Link>
              ) : isAuctionRegistered && !isAwaitingApproval ? (
                <div className="flex w-full flex-row items-center justify-center gap-2 rounded border border-green-700 p-2 text-green-700">
                  <Check color="#2B9A66" size={20} />
                  <p className="text-center text-sm font-semibold">Registered</p>
                </div>
              ) : (
                <div className="flex w-full flex-row items-center justify-center gap-2 rounded border border-green1 p-2 text-green1">
                  <SpinnerCircularFixed color="#2B9A66" size={20} secondaryColor="#FFFFFF" />
                  <p className="text-center text-sm font-semibold">Awaiting Approval</p>
                </div>
              )}

              {isAuctionLive && (
                <Link to={`/auction-live/public/${auction.slug}`}>
                  <Button className="mt-0 w-full" variant="default">
                    View Live Auction
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mb-4 h-[1px] bg-slate-200 lg:mb-0" />

      <div>
        <div className="mb-4">
          <p className="text-xl font-medium text-black">Star Lots</p>
          <p className="text-xs font-normal text-slate-500">{starLots.totalCount} item(s) found</p>
        </div>
        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {starLots.list.map((product, index) => (
            <OptimizedProductCard
              key={index}
              product={product as unknown as Awaited<ReturnType<typeof optimizedGetProductList>>['list'][number]}
            />
          ))}
        </div>
        <CustomPagination
          limit={limit}
          totalCount={starLots.totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevPage={() => {
            searchParams.set('page', (currentPage - 1).toString());
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          onNextPage={() => {
            searchParams.set('page', (currentPage + 1).toString());
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          onPageChange={(page) => {
            searchParams.set('page', page);
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          onLimitChange={(limit) => {
            searchParams.set('limit', limit);
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
        />
      </div>
    </Layout>
  );
}
